import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { ArrowLeft, CreditCard, ExternalLink, Sparkles } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { LibraryVideoPlayer } from "~/components/learning/library-video-player";
import { usePlacementVideo } from "~/components/learning/use-placement-video";
import { NovaPlanRow } from "~/components/nova/nova-plan-row";
import { NOVA_SUBSCRIPTION_QUERY_KEY } from "~/components/nova/use-nova-access";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { authMiddleware } from "~/lib/auth-middleware";
import { cn } from "~/lib/cn";
import {
  type NovaAccessState,
  type NovaPlan,
} from "~/lib/functions/nova-billing";
import { coachStatusQueryOptions } from "~/lib/queries/coachStatusQuery";
import { placementVideosQueryOptions } from "~/lib/queries/learningQuery";
import {
  type BillingSubscription,
  billingStatusMeta,
  formatBillingPrice,
  formatBillingRenewal,
} from "~/lib/shared/billing";
import { createNoIndexMeta } from "~/lib/shared/meta";
import {
  FREE_ACCESS_BILLING_LINE,
  FREE_ACCESS_PRODUCT_LABELS,
  formatFreeAccessDay,
  freeAccessEndDay,
  holdsNovaFreeAccess,
} from "~/lib/shared/nova/free-access";
import {
  BILLING_TERMS,
  conflictingPlanKeys,
  NOVA_GRANTING_PLAN_KEYS,
  shownTerm,
  type BillingTerm,
  type PlanKey,
} from "~/lib/shared/plan-catalogue";
import {
  isChangeablePlan,
  planCardCta,
  type PlanCardServerFacts,
} from "~/lib/shared/plan-change";
import {
  BILLING_NOTICES,
  BILLING_PATH,
  parseReturnPath,
} from "~/lib/shared/return-path";

import { NovaCancelSection } from "./-components/nova-cancel-section";
import { SettingsCardTitle } from "./-components/settings-card-title";

/** Free NOVA Core from Dream Coach Match, while it lasts. */
type BillingFreeAccess = {
  /** The last day of access, "YYYY-MM-DD" in New York; null for no end date. */
  endsOn: string | null;
};

type BillingOverview = {
  subscriptions: BillingSubscription[];
  /** Whether the user has a Stripe customer — i.e. the portal is reachable. */
  hasBillingAccount: boolean;
  /** Null unless staff gave her free access that has not ended. */
  freeAccess: BillingFreeAccess | null;
};

export const getBillingOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<BillingOverview> => {
    const { getCoachByClerkUserId } = await import("~/lib/server/coach");
    const coach = await getCoachByClerkUserId(context.user.clerkUserId);
    if (!coach) {
      return { subscriptions: [], hasBillingAccount: false, freeAccess: null };
    }

    // Shown beside any Stripe subscription, and also without a Stripe
    // customer: free access needs neither.
    const freeAccess: BillingFreeAccess | null = holdsNovaFreeAccess(
      coach,
      new Date(),
    )
      ? {
          endsOn:
            coach.novaFreeAccessUntil === null
              ? null
              : freeAccessEndDay(coach.novaFreeAccessUntil),
        }
      : null;

    // Self-healing: a Stripe customer can exist for this coach without ever
    // having been linked back onto the coach record (e.g. a webhook delivery
    // that was missed) — resolve it by metadata/email before concluding there
    // is no billing account, otherwise the coach loses access to their own
    // subscriptions/invoices for no recoverable reason.
    const { resolveExistingStripeCustomer } =
      await import("~/lib/stripe/customer");
    const stripeCustomerId = await resolveExistingStripeCustomer(coach);
    if (!stripeCustomerId) {
      return { subscriptions: [], hasBillingAccount: false, freeAccess };
    }

    // Self-healing: reconcile Nova's own columns against Stripe on every
    // billing-page visit, so a missed webhook delivery never leaves the coach
    // stuck on stale access. Best-effort — never throws.
    const { syncNovaSubscriptionFromStripe } =
      await import("~/lib/server/nova/subscription/sync");
    await syncNovaSubscriptionFromStripe(
      { ...coach, stripeCustomerId },
      { trigger: "billing_page" },
    );

    const { listCustomerSubscriptions } = await import("~/lib/server/billing");
    return {
      subscriptions: await listCustomerSubscriptions(stripeCustomerId),
      hasBillingAccount: true,
      freeAccess,
    };
  });

type AvailablePlansPayload = {
  plans: NovaPlan[];
  novaAccess: NovaAccessState;
  hasExistingNovaSubscription: boolean;
  /** What the NOVA plan cards are worked out from (`readPlanCards`). */
  planCards: PlanCardServerFacts;
} | null;

/**
 * Plans purchasable from this page. Null for non-coach users — this page is
 * shared with regular users, so it must not require coach auth (the
 * coach-authed Nova server fns would 403 for them). NOVA Pro+ is among them
 * only for an Accelerator graduate (`fetchPlansOnOffer`).
 */
export const getAvailablePlans = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<AvailablePlansPayload> => {
    const { getCoachByClerkUserId } = await import("~/lib/server/coach");
    const coach = await getCoachByClerkUserId(context.user.clerkUserId);
    if (!coach) return null;

    const { isActiveNovaStatus, readNovaSubscription } =
      await import("~/lib/server/nova-billing");
    const { fetchPlansOnOffer } =
      await import("~/lib/server/accelerator/pro-plus");
    const { getNovaAccess } = await import("~/lib/server/nova/access");
    const { readPlanCards } = await import("~/lib/server/nova-plan-change");
    const [plans, access, planCards] = await Promise.all([
      fetchPlansOnOffer(coach),
      getNovaAccess(coach),
      readPlanCards(coach),
    ]);
    const subscription = readNovaSubscription(coach);
    return {
      plans,
      novaAccess: access.state,
      hasExistingNovaSubscription:
        subscription.status !== "none" &&
        isActiveNovaStatus(subscription.status),
      planCards,
    };
  });

export const createBillingPortalSession = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<{ url: string }> => {
    const { getCoachByClerkUserId } = await import("~/lib/server/coach");
    const coach = await getCoachByClerkUserId(context.user.clerkUserId);
    if (!coach) {
      throw new Error("No billing account found for this user.");
    }

    const { resolveExistingStripeCustomer } =
      await import("~/lib/stripe/customer");
    const stripeCustomerId = await resolveExistingStripeCustomer(coach);
    if (!stripeCustomerId) {
      throw new Error("No billing account found for this user.");
    }

    const { getRequestUrl } = await import("@tanstack/react-start/server");
    const { getAppUrl } = await import("~/lib/utils");
    const { createCustomerPortalUrl } = await import("~/lib/server/billing");
    const { createNovaPortalUrl, holdsLiveNovaSubscription } =
      await import("~/lib/server/nova-billing");

    const requestUrl = getRequestUrl();
    const appUrl = getAppUrl(new Request(requestUrl.href));
    // A NOVA coach gets NOVA's billing page, where cancelling is off: she
    // cancels in the app instead.
    const url = holdsLiveNovaSubscription(coach)
      ? await createNovaPortalUrl({
          coach: { ...coach, stripeCustomerId },
          appUrl,
          returnPath: BILLING_PATH,
        })
      : await createCustomerPortalUrl({
          customerId: stripeCustomerId,
          returnUrl: `${appUrl}${BILLING_PATH}`,
        });
    return { url };
  });

export const Route = createFileRoute("/_user/settings/profile/billing")({
  staticData: { title: "Billing" },
  validateSearch: z.object({
    /** The screen a locked click came from. Checkout returns to it, and the
     *  page links back to it. Anything that is not a page in the app is
     *  dropped rather than followed. */
    returnTo: z
      .unknown()
      .transform((value) => parseReturnPath(value) ?? undefined),
    notice: z.enum(BILLING_NOTICES).optional(),
    /** Preselects the billing period, so a visitor who chose Yearly on the
     *  pricing page does not land on a monthly view contradicting it. */
    term: z.enum(BILLING_TERMS).optional(),
  }),
  head: () =>
    createNoIndexMeta(
      "Billing – Dream Coach Match",
      "View your subscriptions and manage billing",
    ),
  loader: async ({ context }) => {
    // The video is for coaches only, and its prefetch only hands SSR the
    // answer: a failed read is a page without the video, never an error page.
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["billing-overview"],
        queryFn: () => getBillingOverview(),
      }),
      context.queryClient
        .fetchQuery(coachStatusQueryOptions(context.user.clerkUserId))
        .then((coachStatus) =>
          coachStatus.isCoach && !coachStatus.isRevoked
            ? context.queryClient.ensureQueryData(placementVideosQueryOptions())
            : undefined,
        )
        .catch(() => undefined),
    ]);
    return {};
  },
  component: BillingPage,
});

/** Its own component so only a coach ever reads the placements. */
function PlansAndPricingVideo() {
  const video = usePlacementVideo("plans_and_pricing");
  return video ? <LibraryVideoPlayer video={video} /> : null;
}

function BillingPage() {
  const queryClient = useQueryClient();
  const { returnTo, notice, term } = Route.useSearch();
  const { user } = Route.useRouteContext();
  const { data: coachStatus } = useQuery(
    coachStatusQueryOptions(user.clerkUserId),
  );
  const { data, isLoading } = useQuery({
    queryKey: ["billing-overview"],
    queryFn: () => getBillingOverview(),
  });

  const availablePlansQuery = useQuery({
    queryKey: ["billing-available-plans"],
    queryFn: () => getAvailablePlans(),
    // Depends on the overview fetch, which runs `syncNovaSubscriptionFromStripe`
    // as a side effect — waiting for it avoids reading the coach's Nova access
    // columns before the sync has landed.
    enabled: data !== undefined,
  });
  const availablePlans = availablePlansQuery.data;

  // The overview's server-side sync updates the DB but never touches the
  // shared Nova access cache, so already-mounted Nova UI (e.g. the studio
  // home) would keep stale access for up to its 5-minute stale time. Refresh
  // it once the sync has had a chance to run.
  const novaAccessRefreshed = useRef(false);
  useEffect(() => {
    if (data === undefined || novaAccessRefreshed.current) return;
    novaAccessRefreshed.current = true;
    void queryClient.invalidateQueries({
      queryKey: NOVA_SUBSCRIPTION_QUERY_KEY,
    });
  }, [data, queryClient]);

  const portalMutation = useMutation({
    mutationFn: () => createBillingPortalSession(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: () => {
      toast.error("Could not open the billing portal. Please try again.");
    },
  });

  if (isLoading) {
    return <BillingSkeleton />;
  }

  const subscriptions = data?.subscriptions ?? [];
  const hasBillingAccount = data?.hasBillingAccount ?? false;
  const freeAccess = data?.freeAccess ?? null;
  // Which plans the coach is already on, so the list offers a plan once.
  // Straight from each subscription, which resolved its own plan from the
  // product's `product_kind`. Deriving it from the sellable catalogue instead
  // would go blind exactly when it matters: a plan whose prices are inactive
  // or failing validation drops out of that list, and a grandfathered
  // subscriber has no lookup key either — so the page would offer a plan the
  // coach already holds and the server would then reject the checkout.
  const subscribedPlanKeys = new Set<PlanKey>(
    subscriptions.flatMap((subscription) =>
      subscription.planKey ? [subscription.planKey] : [],
    ),
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      {returnTo && <BackToReturnPath returnTo={returnTo} />}
      {notice === "nova-subscription-exists" && (
        <Card role="status" className="text-text-secondary p-4 text-sm">
          You already have a Nova subscription. We did not open another
          checkout. Manage your existing subscription in billing before choosing
          the bundle to avoid overlapping charges.
        </Card>
      )}
      {notice === "nova-subscription-unpaid" && (
        <p
          role="status"
          className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          Your Nova subscription is paused because a payment did not go through.
          Update your payment method below to restore access — starting a second
          subscription would charge you twice and leave the unpaid invoice open.
          Your work is untouched.
        </p>
      )}
      {notice === "plan-already-held" && (
        <Card role="status" className="text-text-secondary p-4 text-sm">
          You already subscribe to this plan, so we did not open another
          checkout. Manage your current subscription in billing to change how it
          is billed.
        </Card>
      )}
      {coachStatus?.isCoach && !coachStatus.isRevoked && (
        <PlansAndPricingVideo />
      )}
      <Card>
        <SettingsCardTitle icon={CreditCard}>
          Subscriptions &amp; billing
        </SettingsCardTitle>

        {subscriptions.length === 0 && freeAccess === null ? (
          <BillingEmptyState />
        ) : (
          <ul className="divide-y divide-gray-100">
            {freeAccess && <FreeAccessRow freeAccess={freeAccess} />}
            {subscriptions.map((subscription) => (
              <SubscriptionRow
                key={subscription.id}
                subscription={subscription}
              />
            ))}
          </ul>
        )}

        {/* The portal also exposes invoices, payment methods and past plans,
            so offer it to anyone with a billing account, even if they have no
            active subscription right now. */}
        {hasBillingAccount && (
          <Button
            className="self-start"
            variant={subscriptions.length === 0 ? "default" : "primary"}
            size="sm"
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isPending}
          >
            {portalMutation.isPending ? (
              "Opening…"
            ) : (
              <>
                Manage billing
                <ExternalLink className="ml-1 size-3.5" />
              </>
            )}
          </Button>
        )}
      </Card>
      {coachStatus?.isCoach && !coachStatus.isRevoked && (
        <NovaCancelSection userId={user.clerkUserId} />
      )}

      {availablePlansQuery.isError ? (
        <Card>
          <p role="alert" className="text-text-secondary text-sm">
            We couldn’t load the available plans.
          </p>
          <Button
            onClick={() => void availablePlansQuery.refetch()}
            disabled={availablePlansQuery.isFetching}
          >
            Retry available plans
          </Button>
        </Card>
      ) : availablePlansQuery.isLoading ? (
        <p role="status" className="text-text-secondary text-sm">
          Loading available plans…
        </p>
      ) : availablePlans && availablePlans.plans.length > 0 ? (
        <AvailablePlans
          // The toggle starts from the coach's held term, and the coach's own
          // choice stays after that. A switch navigates here before the
          // queries refresh, so the page first mounts on the old term; a new
          // held term remounts it.
          key={availablePlans.planCards.current?.term}
          plans={availablePlans.plans}
          novaAccess={availablePlans.novaAccess}
          hasExistingNovaSubscription={
            availablePlans.hasExistingNovaSubscription ||
            // Every plan that grants Nova, both terms — the previous literal
            // pair missed the annual keys entirely, and any grandfathered
            // subscription, whose price carries no lookup key at all.
            NOVA_GRANTING_PLAN_KEYS.some((plan) =>
              subscribedPlanKeys.has(plan),
            ) ||
            notice === "nova-subscription-exists"
          }
          subscribedPlanKeys={subscribedPlanKeys}
          initialTerm={
            term ??
            availablePlans.planCards.current?.term ??
            availablePlans.planCards.suite?.term ??
            availablePlans.planCards.winback?.trial?.term ??
            availablePlans.planCards.winback?.lastPlan?.term ??
            "monthly"
          }
          planCards={availablePlans.planCards}
          returnTo={returnTo}
          onManageBilling={() => portalMutation.mutate()}
          isManagingBilling={portalMutation.isPending}
        />
      ) : null}
    </motion.div>
  );
}

/**
 * Which plans a coach is offered, and why.
 *
 * NOVA Pro contains both Nova and the Marketing Suite, so it must not be sold
 * to a coach holding either — nor the standalone Suite to a Pro holder. Both
 * directions would bill the same entitlement twice. Nova and the standalone
 * Suite grant disjoint entitlements, which is what makes the Suite a genuine
 * upsell to a Nova subscriber rather than a duplicate.
 *
 * A plan the coach already holds stays visible with its Active badge. A plan
 * that would duplicate something they hold is shown blocked, with a route to
 * billing, rather than hidden — a coach who can see the bundle in the pricing
 * page should find out here why they cannot buy it, not just fail to find it.
 */
function visiblePlans(
  plans: NovaPlan[],
  subscribedPlanKeys: ReadonlySet<PlanKey>,
): Array<{ plan: NovaPlan; upsellReason?: string; conflictsWith?: PlanKey }> {
  const hasNova = subscribedPlanKeys.has("nova");
  return plans.map((plan) => {
    if (subscribedPlanKeys.has(plan.planKey)) return { plan };
    const conflict = conflictingPlanKeys(plan.planKey).find(
      (candidate) =>
        candidate !== plan.planKey && subscribedPlanKeys.has(candidate),
    );
    if (conflict) return { plan, conflictsWith: conflict };
    if (plan.planKey === "marketing_suite") {
      return {
        plan,
        upsellReason: hasNova
          ? "Add the CRM, funnels, email and booking that Nova writes into. Runs alongside your Nova subscription."
          : "CRM, funnels, email, booking and courses in one login.",
      };
    }
    if (plan.planKey === "nova_pro") {
      return {
        plan,
        upsellReason: "Nova and the Marketing Suite on one subscription.",
      };
    }
    // Sent only to an Accelerator graduate. Her graduate price line already
    // says why she sees it, so a discounted card needs no second line.
    if (plan.planKey === "nova_pro_plus") {
      return plan.graduateDiscount
        ? { plan }
        : {
            plan,
            upsellReason: "For Business Builder Accelerator graduates.",
          };
    }
    return { plan };
  });
}

function AvailablePlans({
  plans,
  novaAccess,
  hasExistingNovaSubscription,
  subscribedPlanKeys,
  initialTerm,
  planCards,
  returnTo,
  onManageBilling,
  isManagingBilling,
}: {
  plans: NovaPlan[];
  novaAccess: NovaAccessState;
  hasExistingNovaSubscription: boolean;
  subscribedPlanKeys: ReadonlySet<PlanKey>;
  initialTerm: BillingTerm;
  planCards: PlanCardServerFacts;
  returnTo: string | undefined;
  onManageBilling: () => void;
  isManagingBilling: boolean;
}) {
  const [term, setTerm] = useState<BillingTerm>(initialTerm);
  const rows = visiblePlans(plans, subscribedPlanKeys);
  // Every rendered row must offer BOTH terms before a selector appears.
  // `shownTerm` falls back to a plan's other term when the selected one is
  // unconfigured, and that cuts both ways: a monthly-only row would sell
  // monthly under "Yearly", and a yearly-only row — which `fetchNovaPlans`
  // deliberately permits — would sell an annual subscription under the default
  // "Monthly". Either way the selector would name a term the row does not
  // charge.
  const everyRowHasBothTerms =
    rows.length > 0 &&
    rows.every(({ plan }) => plan.monthly !== null && plan.yearly !== null);

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <SettingsCardTitle icon={Sparkles}>Available plans</SettingsCardTitle>

        {everyRowHasBothTerms && (
          <div
            role="radiogroup"
            aria-label="Billing period"
            className="flex rounded-full border border-gray-200 p-0.5"
          >
            {(["monthly", "yearly"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                role="radio"
                aria-checked={term === option}
                variant={term === option ? "primary" : "ghost"}
                size="sm"
                onClick={() => setTerm(option)}
                className="rounded-full capitalize"
              >
                {option}
              </Button>
            ))}
          </div>
        )}
      </div>

      <ul className="divide-y divide-gray-100">
        {rows.map(({ plan, upsellReason, conflictsWith }) => (
          <NovaPlanRow
            key={plan.planKey}
            plan={plan}
            term={term}
            novaAccess={novaAccess}
            hasExistingNovaSubscription={hasExistingNovaSubscription}
            subscribed={subscribedPlanKeys.has(plan.planKey)}
            upsellReason={upsellReason}
            conflictsWith={conflictsWith}
            lastPlan={planCards.winback?.lastPlan?.plan === plan.planKey}
            cta={
              isChangeablePlan(plan.planKey)
                ? planCardCta(plan.planKey, {
                    ...planCards,
                    subscribedPlans: subscribedPlanKeys,
                    // The term the row shows and sells, which is not the
                    // toggle's when the plan has no price on that term.
                    selectedTerm: shownTerm(plan, term),
                    pricedTerms: new Set(
                      BILLING_TERMS.filter(
                        (candidate) => plan[candidate] !== null,
                      ),
                    ),
                  })
                : undefined
            }
            returnTo={returnTo}
            onManageBilling={onManageBilling}
            isManagingBilling={isManagingBilling}
          />
        ))}
      </ul>
    </Card>
  );
}

/**
 * The way back to the screen that sent the coach here. The path is parsed with
 * the router's own search parser, so the screen gets back exactly the search it
 * had.
 */
function BackToReturnPath({ returnTo }: { returnTo: string }) {
  const router = useRouter();
  const queryStart = returnTo.indexOf("?");
  const pathname = queryStart === -1 ? returnTo : returnTo.slice(0, queryStart);
  const searchStr = queryStart === -1 ? "" : returnTo.slice(queryStart);
  return (
    <Link
      to={pathname}
      search={router.options.parseSearch(searchStr)}
      className="text-text-secondary inline-flex w-fit items-center gap-1.5 text-sm transition-colors hover:text-foreground"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Back to where you were
    </Link>
  );
}

function SubscriptionRow({
  subscription,
}: {
  subscription: BillingSubscription;
}) {
  const status = billingStatusMeta(subscription.status);
  const renewal = formatBillingRenewal(subscription);

  return (
    <li className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div className="min-w-0 space-y-1">
        <p className="truncate text-base font-medium text-gray-900">
          {subscription.productName}
        </p>
        <p className="text-text-tertiary text-sm">
          {formatBillingPrice(subscription)}
        </p>
        {renewal && <p className="text-text-tertiary text-xs">{renewal}</p>}
      </div>
      <span className="flex shrink-0 items-center gap-1.5">
        <span className={cn("size-1.5 rounded-full", status.dot)} />
        <span className={cn("text-xs font-medium", status.text)}>
          {status.label}
        </span>
      </span>
    </li>
  );
}

/** Free access as its own line: no price, no renewal, nothing to cancel. */
function FreeAccessRow({ freeAccess }: { freeAccess: BillingFreeAccess }) {
  return (
    <li className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div className="min-w-0 space-y-1">
        <p className="truncate text-base font-medium text-gray-900">
          {FREE_ACCESS_PRODUCT_LABELS.nova}
        </p>
        <p className="text-text-tertiary text-sm">{FREE_ACCESS_BILLING_LINE}</p>
        {freeAccess.endsOn && (
          <p className="text-text-tertiary text-xs">
            Until {formatFreeAccessDay(freeAccess.endsOn)}
          </p>
        )}
      </div>
      <span className="flex shrink-0 items-center gap-2">
        <span className="size-1.5 rounded-full bg-emerald-500" />
        <span className="text-xs font-medium text-emerald-700">Free</span>
      </span>
    </li>
  );
}

function BillingEmptyState() {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl bg-gray-50 p-6 text-center">
      <p className="font-medium text-gray-900">No active subscriptions</p>
      <p className="text-text-tertiary max-w-sm text-sm">
        When you subscribe to a plan, it’ll show up here and you’ll be able to
        manage it anytime.
      </p>
    </div>
  );
}

function BillingSkeleton() {
  return (
    <Card>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-9 w-32" />
    </Card>
  );
}
